import Joi from 'joi';

import { ContactTitleEnum } from '../../../../enums/contact-title.enum';
import { legalAgeSchema } from '../../../../validators/schemas/legal-age.schema';
import { phoneSchema } from '../../../../validators/schemas/phone.schema';

/**
 * Schema for contact validation belonging to a customer with type set to individual.
 * @param legalAge - The legal age to be used for validation.
 * @returns {Joi.ObjectSchema} - A Joi object schema for validating individual customer contact data.
 */
export const createIndividualCustomerContactSchema = (legalAge: number) =>
  Joi.object({
    id: Joi.valid(null).optional().messages({
      'any.only': 'validations|contact-id|must-be-null',
    }),

    firstName: Joi.alternatives().conditional(Joi.any().valid(null), {
      then: Joi.invalid(null).messages({
        'any.invalid': 'validations|contact-first-name|cannot-be-null',
      }),
      otherwise: Joi.string().required().messages({
        'any.required': 'validations|contact-first-name|required',
        'string.base': 'validations|contact-first-name|must-be-string',
      }),
    }),

    middleNames: Joi.string().allow(null).messages({
      'string.base': 'validations|contact-middle-names|must-be-string',
    }),

    lastName: Joi.string().allow(null).messages({
      'string.base': 'validations|contact-last-name|must-be-string',
    }),

    title: Joi.alternatives().conditional(Joi.any().valid(null), {
      then: Joi.invalid(null).messages({
        'any.invalid': 'validations|contact-title|cannot-be-null',
      }),
      otherwise: Joi.string()
        .valid(...Object.values(ContactTitleEnum))
        .required()
        .messages({
          'any.required': 'validations|contact-title|required',
          'any.only': 'validations|contact-title|invalid-value',
          'string.base': 'validations|contact-title|must-be-string',
        }),
    }),

    dateOfBirth: Joi.alternatives()
      .try(Joi.valid(null), legalAgeSchema(legalAge))
      .messages({
        'any.required': 'validations|contact-date-of-birth|required',
        'date.base': 'validations|contact-date-of-birth|must-be-a-valid-date',
        'date.less': 'validations|contact-date-of-birth|must-be-in-the-past',
        'age.underage': 'validations|contact-date-of-birth|underage',
      }),

    email: Joi.string()
      .email({ tlds: { allow: false } })
      .allow(null)
      .messages({
        'string.base': 'validations|contact-email|must-be-string',
        'string.email': 'validations|contact-email|invalid',
      }),

    address1: Joi.string().allow(null).messages({
      'string.base': 'validations|contact-address1|must-be-string',
    }),

    address2: Joi.string().allow(null).messages({
      'string.base': 'validations|contact-address2|must-be-string',
    }),

    phoneLandline: phoneSchema().messages({
      'string.pattern.base': 'validations|contact-phone-landline|invalid',
      'string.base': 'validations|contact-phone-landline|must-be-string',
    }),

    phoneMobile: phoneSchema().messages({
      'string.pattern.base': 'validations|contact-phone-mobile|invalid',
      'string.base': 'validations|contact-phone-mobile|must-be-string',
    }),

    phoneFax: phoneSchema().messages({
      'string.pattern.base': 'validations|contact-phone-fax|invalid',
      'string.base': 'validations|contact-phone-fax|must-be-string',
    }),

    city: Joi.string().allow(null).messages({
      'string.base': 'validations|contact-city|must-be-string',
    }),

    postCode: Joi.string().allow(null).messages({
      'string.base': 'validations|contact-post-code|must-be-string',
    }),

    poBox: Joi.string().allow(null).messages({
      'string.base': 'validations|contact-po-box|must-be-string',
    }),

    county: Joi.string().allow(null).messages({
      'string.base': 'validations|contact-county|must-be-string',
    }),

    state: Joi.string().allow(null).messages({
      'string.base': 'validations|contact-state|must-be-string',
    }),

    countryId: Joi.number().allow(null).messages({
      'number.base': 'validations|contact-country-id|must-be-number',
    }),

    website: Joi.string().allow(null).messages({
      'string.base': 'validations|contact-website|must-be-string',
    }),

    facebook: Joi.string().allow(null).messages({
      'string.base': 'validations|contact-facebook|must-be-string',
    }),

    instagram: Joi.string().allow(null).messages({
      'string.base': 'validations|contact-instagram|must-be-string',
    }),

    twitter_x: Joi.string().allow(null).messages({
      'string.base': 'validations|contact-twitter_x|must-be-string',
    }),

    slack: Joi.string().allow(null).messages({
      'string.base': 'validations|contact-slack|must-be-string',
    }),

    whatsApp: Joi.string().allow(null).messages({
      'string.base': 'validations|contact-whatsapp|must-be-string',
    }),

    youtube: Joi.string().allow(null).messages({
      'string.base': 'validations|contact-youtube|must-be-string',
    }),

    linkedIn: Joi.string().allow(null).messages({
      'string.base': 'validations|contact-linkedin|must-be-string',
    }),
  })
    // Ignore extra fields
    .unknown();
